import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { PropertyField } from '../../../../shared/components/property-panel/property-panel.model';
import { GlobalFilterColumnOption, GlobalFilterSelection } from '../global-filter-modal/global-filter-modal.component';
import {
  VisualColumnGroup,
  VisualColumnOption,
  VisualDataset,
  VisualElementDraft,
  VisualElementLink,
  DEFAULT_VISUAL_ORDER,
  DEFAULT_VISUAL_WIDTH,
  MIN_VISUAL_WIDTH,
  MAX_VISUAL_WIDTH,
  INTERNAL_LINK_BASE_HREF,
  buildColumnGroups,
  cloneVisualElement,
  createEmptyVisualElement,
  createEmptyVisualElementLink,
  getElementColumnLabel,
  getDefaultCountColumn,
  parseDatasets
} from './add-visual.model';

/**
 * "Visual Editor" body of the Add visual popup. The element config is an array so more elements can be
 * added later: a single element is rendered directly, several are rendered as an accordion.
 */
@Component({
  selector: 'app-add-visual',
  standalone: false,
  templateUrl: './add-visual.component.html',
  // Reuses the add-filter look (toolbar buttons, inputs, selects and the data-column picker panel).
  styleUrls: ['../add-filter/add-filter.component.scss', './add-visual.component.scss']
})
export class AddVisualComponent implements OnChanges {
  @Input() draft: VisualElementDraft[] | null = null;
  @Output() draftChange = new EventEmitter<VisualElementDraft[]>();
  // Report's datasource config (object, or JSON string) - { datasets: [{ id, name, table_name, columns: [...] }] }.
  @Input() selectedDataSource: any = null;
  // Names of the filters the report page already has - the options of "Applied filters".
  @Input() availableFilters: string[] = [];
  @Input() heading = '';
  @Output() headingChange = new EventEmitter<string>();
  @Input() chartOrder = DEFAULT_VISUAL_ORDER;
  @Input() chartWidth = DEFAULT_VISUAL_WIDTH;
  @Input() currentVisualCount = 0;
  // Gates the optional "Add link to key" (valuesLink) control in Manage chart properties.
  @Input() hasReportAdminRole = false;
  @Input() visualTypeLabel = '';
  @Output() chartOrderChange = new EventEmitter<number>();
  @Output() chartWidthChange = new EventEmitter<number>();

  elements: VisualElementDraft[] = [];
  columnGroups: VisualColumnGroup[] = [];
  globalFilterColumnOptions: GlobalFilterColumnOption[] = [];
  // Cached (not getters) so mat-select option lists keep a stable identity between change detections.
  filterOptions: string[] = [];
  expandedIds = new Set<string>();
  // Search text per picker (`${elementId}:columns` / `${elementId}:count`).
  searchTerm: { [pickerId: string]: string } = {};
  defaultAttributeSearch: { [elementId: string]: string } = {};
  chartPropertyPanelOpen = false;
  chartPropertyFields: PropertyField[] = [];
  chartPropertyValues: { [key: string]: string | number | VisualElementLink } = { order: String(this.chartOrder), width: this.chartWidth };

  private datasets: VisualDataset[] = [];
  private optionsByKey = new Map<string, VisualColumnOption>();
  private autoCountKeys = new Map<string, string>();
  private readonly collapsedGroups = new Set<string>();
  private readonly filteredGroupsCache = new Map<string, { term: string; groups: VisualColumnGroup[] }>();
  // `draftChange` round-trips through the parent back into `[draft]` - ignore that echo so the rows
  // being edited aren't replaced on every change.
  private suppressNextDraftSync = false;
  private activeFilterElementId: string | null = null;

  @ViewChild('globalFilterModal') private globalFilterModal: any;
  @ViewChild('globalFilterModalBody') private globalFilterModalBody: any;
  @ViewChild('tableFilterModal') private tableFilterModal: any;
  @ViewChild('tableFilterModalBody') private tableFilterModalBody: any;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDataSource']) {
      this.datasets = parseDatasets(this.selectedDataSource);
      this.columnGroups = buildColumnGroups(this.datasets);
      this.filteredGroupsCache.clear();
      this.optionsByKey = new Map(this.columnGroups.flatMap(group => group.columns.map(column => [column.key, column] as const)));
      this.globalFilterColumnOptions = this.columnGroups.flatMap(group => group.columns.map(column => ({
        key: column.key,
        label: column.displayName,
        tableName: column.tableName,
        columnName: column.columnName,
        datasetId: column.datasetId,
        tableLabel: column.tableName || group.group
      })));
    }
    if (changes['draft']) {
      if (this.suppressNextDraftSync) {
        this.suppressNextDraftSync = false;
      } else {
        this.elements = this.draft?.length ? this.draft.map(cloneVisualElement) : [createEmptyVisualElement()];
        this.autoCountKeys.clear();
        if (!this.elements.some(element => this.expandedIds.has(element.id))) {
          this.expandedIds = new Set([this.elements[0].id]);
        }
      }
    }
    if (changes['availableFilters'] || changes['draft']) {
      this.refreshFilterOptions();
    }
    if (changes['chartOrder'] || changes['chartWidth']) {
      this.chartPropertyValues = { order: String(this.chartOrder), width: this.chartWidth };
    }
  }

  get isAccordion(): boolean {
    return this.elements.length > 1;
  }

  trackByElementId(_index: number, element: VisualElementDraft): string {
    return element.id;
  }

  isExpanded(element: VisualElementDraft): boolean {
    return this.expandedIds.has(element.id);
  }

  toggleExpanded(element: VisualElementDraft): void {
    const next = new Set(this.expandedIds);
    if (!next.delete(element.id)) { next.add(element.id); }
    this.expandedIds = next;
  }

  getElementSummary(element: VisualElementDraft): string {
    const count = element.columns.length;
    return `${count} column${count === 1 ? '' : 's'}`;
  }

  // ---- Data columns (multi-select) ----

  getColumnsLabel(element: VisualElementDraft): string {
    return element.columns.map(key =>
      this.optionsByKey.get(key)?.displayName || this.getLabel(element, key)
    ).join(', ');
  }

  onHeadingChange(heading: string): void {
    this.headingChange.emit(heading);
  }

  getSelectedColumnCountLabel(element: VisualElementDraft): string {
    const count = element.columns.length;
    return count === 1 ? '1 selected column' : `${count} selected columns`;
  }

  getLabel(element: VisualElementDraft, key: string): string {
    return getElementColumnLabel(element, this.optionsByKey.get(key), key);
  }

  isColumnSelected(element: VisualElementDraft, key: string): boolean {
    return element.columns.includes(key);
  }

  toggleColumn(element: VisualElementDraft, key: string): void {
    this.onColumnsChange(
      element,
      element.columns.includes(key) ? element.columns.filter(existing => existing !== key) : [...element.columns, key]
    );
  }

  areAllVisibleSelected(element: VisualElementDraft): boolean {
    const visible = this.getFilteredGroups(this.pickerId(element, 'columns')).flatMap(group => group.columns.map(column => column.key));
    return visible.length > 0 && visible.every(key => element.columns.includes(key));
  }

  areSomeVisibleSelected(element: VisualElementDraft): boolean {
    const visible = this.getFilteredGroups(this.pickerId(element, 'columns')).flatMap(group => group.columns.map(column => column.key));
    const selectedCount = visible.filter(key => element.columns.includes(key)).length;
    return selectedCount > 0 && selectedCount < visible.length;
  }

  toggleSelectAllVisible(element: VisualElementDraft, checked: boolean): void {
    const visible = this.getFilteredGroups(this.pickerId(element, 'columns')).flatMap(group => group.columns.map(column => column.key));
    const visibleKeys = new Set(visible);
    const remaining = element.columns.filter(key => !visibleKeys.has(key));
    this.onColumnsChange(element, checked ? [...remaining, ...visible] : remaining);
  }

  onColumnsChange(element: VisualElementDraft, selection: string[] | null): void {
    const previousColumns = new Set(element.columns);
    element.columns = [...(selection || [])];
    // Keep user deselections, but select new columns as default attributes.
    element.defaultAttributes = element.columns.filter(key =>
      !previousColumns.has(key) || element.defaultAttributes.includes(key));
    if (!element.totalCountColumn || element.totalCountColumn === this.autoCountKeys.get(element.id)) {
      element.totalCountColumn = getDefaultCountColumn(element, this.datasets);
      if (element.totalCountColumn) {
        this.autoCountKeys.set(element.id, element.totalCountColumn);
      } else {
        this.autoCountKeys.delete(element.id);
      }
    }
    this.clearStaleLinkKey(element);
    this.onFieldChange();
  }

  // ---- Total count (single select + distinct) ----

  getTotalCountLabel(element: VisualElementDraft): string {
    return element.totalCountColumn ? this.getLabel(element, element.totalCountColumn) : '';
  }

  onTotalCountColumnChange(element: VisualElementDraft, key: string | null): void {
    element.totalCountColumn = key || null;
    this.autoCountKeys.delete(element.id);
    this.onFieldChange();
  }

  onDistinctChange(element: VisualElementDraft, distinct: boolean): void {
    element.totalCountDistinct = !!distinct;
    this.onFieldChange();
  }

  // ---- Applied filters / attributes ----

  onAppliedFiltersChange(element: VisualElementDraft, selection: string[] | null): void {
    element.appliedFilters = [...(selection || [])];
    this.onFieldChange();
  }

  onAttributeVisibleChange(element: VisualElementDraft, visible: boolean): void {
    element.isAttributeVisible = !!visible;
    this.onFieldChange();
  }

  onDefaultAttributesChange(element: VisualElementDraft, selection: string[] | null): void {
    element.defaultAttributes = (selection || []).filter(key => element.columns.includes(key));
    this.clearStaleLinkKey(element);
    this.onFieldChange();
  }

  // A link key can only point at a currently-selected column.
  private clearStaleLinkKey(element: VisualElementDraft): void {
    if (element.linkConfig?.key && !element.columns.includes(element.linkConfig.key)) {
      element.linkConfig = { ...element.linkConfig, key: null };
    }
  }

  getDefaultAttributesLabel(element: VisualElementDraft): string {
    return element.defaultAttributes.map(key => this.getLabel(element, key)).join(', ');
  }

  isDefaultAttributeSelected(element: VisualElementDraft, key: string): boolean {
    return element.defaultAttributes.includes(key);
  }

  toggleDefaultAttribute(element: VisualElementDraft, key: string): void {
    this.onDefaultAttributesChange(
      element,
      element.defaultAttributes.includes(key)
        ? element.defaultAttributes.filter(existing => existing !== key)
        : [...element.defaultAttributes, key]
    );
  }

  areAllDefaultAttributesSelected(element: VisualElementDraft): boolean {
    const visible = this.getFilteredDefaultAttributes(element);
    return visible.length > 0 && visible.every(key => element.defaultAttributes.includes(key));
  }

  areSomeDefaultAttributesSelected(element: VisualElementDraft): boolean {
    const visible = this.getFilteredDefaultAttributes(element);
    const selectedCount = visible.filter(key => element.defaultAttributes.includes(key)).length;
    return selectedCount > 0 && selectedCount < visible.length;
  }

  toggleSelectAllDefaultAttributes(element: VisualElementDraft, checked: boolean): void {
    const visible = this.getFilteredDefaultAttributes(element);
    const visibleKeys = new Set(visible);
    const remaining = element.defaultAttributes.filter(key => !visibleKeys.has(key));
    this.onDefaultAttributesChange(element, checked ? [...remaining, ...visible] : remaining);
  }

  onDefaultAttributeSearchChange(element: VisualElementDraft, term: string): void {
    this.defaultAttributeSearch[element.id] = term;
  }

  getFilteredDefaultAttributes(element: VisualElementDraft): string[] {
    const term = (this.defaultAttributeSearch[element.id] || '').trim().toLowerCase();
    return element.columns.filter(key => !term || this.getLabel(element, key).toLowerCase().includes(term));
  }

  onDefaultAttributeSelectOpened(element: VisualElementDraft, opened: boolean): void {
    if (!opened) { delete this.defaultAttributeSearch[element.id]; }
  }

  trackByKey(_index: number, key: string): string {
    return key;
  }

  openChartProperties(): void {
    // Manage order offers plain 1..max positions among the page/tab's other non-filter visuals
    // (max = currentVisualCount existing visuals + 1 slot for this one).
    const maxOrder = Math.max(1, Math.trunc(Number(this.currentVisualCount) || 0) + 1);
    this.chartPropertyFields = [
      {
        key: 'order',
        label: 'Manage order',
        type: 'select',
        options: Array.from({ length: maxOrder }, (_unused, index) => {
          const value = String(index + 1);
          return { value, label: value };
        })
      },
      { key: 'width', label: 'Width', type: 'slider', min: MIN_VISUAL_WIDTH, max: MAX_VISUAL_WIDTH, color: '#013b71' }
    ];
    this.chartPropertyValues = { order: String(this.chartOrder), width: this.chartWidth };

    // Report-admin-only: lets one of the selected columns become a clickable link.
    const element = this.elements[0];
    if (this.hasReportAdminRole && this.visualTypeLabel === 'Table' && element) {
      this.chartPropertyFields.push({
        key: 'link',
        label: 'Add link to key',
        type: 'link-config',
        options: element.columns.map(key => ({ value: key, label: this.getLabel(element, key) })),
        baseHref: '{base_url}/'
      });
      this.chartPropertyValues['link'] = element.linkConfig ?? createEmptyVisualElementLink();
    }
    this.chartPropertyPanelOpen = true;
  }

  onChartPropertyValueChange(event: { key: string; value: any }): void {
    if (event.key === 'order') {
      const order = Number(event.value);
      if (!Number.isInteger(order) || order < 1) { return; }
      this.chartPropertyValues = { ...this.chartPropertyValues, order: String(order) };
      this.chartOrderChange.emit(order);
    } else if (event.key === 'width') {
      const width = Math.min(MAX_VISUAL_WIDTH, Math.max(MIN_VISUAL_WIDTH, Math.trunc(Number(event.value))));
      if (!Number.isInteger(width)) { return; }
      this.chartPropertyValues = { ...this.chartPropertyValues, width };
      this.chartWidthChange.emit(width);
    } else if (event.key === 'link') {
      const element = this.elements[0];
      if (!element) { return; }
      element.linkConfig = { ...(event.value as VisualElementLink) };
      this.chartPropertyValues = { ...this.chartPropertyValues, link: element.linkConfig };
      this.onFieldChange();
    }
  }

  closeChartProperties(): void {
    this.chartPropertyPanelOpen = false;
  }

  // ---- Column picker panel (shared by the data column and total count selects) ----

  pickerId(element: VisualElementDraft, picker: 'columns' | 'count'): string {
    return `${element.id}:${picker}`;
  }

  onPickerOpenedChange(pickerId: string, opened: boolean): void {
    if (!opened) {
      delete this.searchTerm[pickerId];
      this.filteredGroupsCache.delete(pickerId);
    }
  }

  onSearchChange(pickerId: string, term: string): void {
    this.searchTerm[pickerId] = term;
    this.cacheFilteredGroups(pickerId, term);
  }

  getFilteredGroups(pickerId: string): VisualColumnGroup[] {
    const term = (this.searchTerm[pickerId] || '').trim().toLowerCase();
    if (!term) { return this.columnGroups; }
    const cached = this.filteredGroupsCache.get(pickerId);
    return cached?.term === term ? cached.groups : this.cacheFilteredGroups(pickerId, term);
  }

  private cacheFilteredGroups(pickerId: string, rawTerm: string): VisualColumnGroup[] {
    const term = rawTerm.trim().toLowerCase();
    const groups = term
      ? this.columnGroups
        .map(group => ({
          group: group.group,
          columns: group.columns.filter(column =>
            column.displayName.toLowerCase().includes(term) || column.columnName.toLowerCase().includes(term))
        }))
        .filter(group => group.columns.length > 0)
      : this.columnGroups;
    this.filteredGroupsCache.set(pickerId, { term, groups });
    return groups;
  }

  trackByColumnGroup(index: number, group: VisualColumnGroup): string {
    return `${index}:${group.group}`;
  }

  trackByColumnOption(_index: number, column: VisualColumnOption): string {
    return column.key;
  }

  // "Type • Unique" style subtitle under each column name.
  getColumnMeta(column: VisualColumnOption): string {
    const parts: string[] = [];
    if (column.type) { parts.push(column.type.charAt(0).toUpperCase() + column.type.slice(1).toLowerCase()); }
    if (column.isPrimaryKey) { parts.push('Unique'); }
    return parts.join(' • ');
  }

  isGroupCollapsed(pickerId: string, group: VisualColumnGroup): boolean {
    return this.collapsedGroups.has(`${pickerId}:${group.group}`);
  }

  toggleGroup(pickerId: string, group: VisualColumnGroup): void {
    const key = `${pickerId}:${group.group}`;
    if (!this.collapsedGroups.delete(key)) { this.collapsedGroups.add(key); }
  }

  // ---- Global / table filter popups ----

  onOpenGlobalFilter(element: VisualElementDraft): void {
    this.activeFilterElementId = element.id;
    this.globalFilterModalBody?.resetState(element.globalFilters);
    this.globalFilterModal?.show();
  }

  onGlobalFilterCancelled(): void {
    this.globalFilterModal?.hide();
  }

  onGlobalFilterSaved(selections: GlobalFilterSelection[]): void {
    const element = this.elements.find(candidate => candidate.id === this.activeFilterElementId);
    if (element) {
      element.globalFilters = this.cloneSelections(selections);
      element.globalFiltersTouched = true;
      this.onFieldChange();
    }
    this.globalFilterModal?.hide();
  }

  onOpenTableFilter(element: VisualElementDraft): void {
    this.activeFilterElementId = element.id;
    this.tableFilterModalBody?.resetState(element.tableFilters);
    this.tableFilterModal?.show();
  }

  onTableFilterCancelled(): void {
    this.tableFilterModal?.hide();
  }

  onTableFilterSaved(selections: GlobalFilterSelection[]): void {
    const element = this.elements.find(candidate => candidate.id === this.activeFilterElementId);
    if (element) {
      element.tableFilters = this.cloneSelections(selections);
      element.tableFiltersTouched = true;
      this.onFieldChange();
    }
    this.tableFilterModal?.hide();
  }

  getFilterCountLabel(selections: GlobalFilterSelection[]): string {
    return selections.length ? ` (${selections.length})` : '';
  }

  private cloneSelections(selections: GlobalFilterSelection[]): GlobalFilterSelection[] {
    return selections.map(selection => ({ ...selection, selectedValues: [...selection.selectedValues] }));
  }

  // ---- Change propagation ----

  private onFieldChange(): void {
    this.refreshFilterOptions();
    this.suppressNextDraftSync = true;
    this.draftChange.emit(this.elements.map(cloneVisualElement));
  }

  // Page filters plus any already referenced by the elements (e.g. loaded from the JSON editor).
  private refreshFilterOptions(): void {
    const next = [...new Set([
      ...(this.availableFilters || []),
      ...this.elements.flatMap(element => element.appliedFilters)
    ])];
    if (next.join('\u0000') !== this.filterOptions.join('\u0000')) {
      this.filterOptions = next;
    }
  }
}
